import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaStripe, FaKey, FaExternalLinkAlt, FaCheckCircle, FaExclamationTriangle,
  FaCopy, FaCheck, FaShieldAlt, FaArrowRight, FaCreditCard, FaUsers
} from 'react-icons/fa';
import { BrandLogo } from '../../components/BrandLogo';

export default function StripeSetupGuide() {
  const [copiedItem, setCopiedItem] = useState(null);

  const copyToClipboard = async (text, item) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const steps = [
    {
      id: 1,
      title: "Create or Access Your Stripe Account",
      description: "You'll need a Stripe account to process real payments.",
      instructions: [
        "Go to stripe.com and sign in or create an account",
        "Complete your business profile with accurate information",
        "Verify your identity as required by Stripe"
      ],
      links: [
        { text: "Go to Stripe Dashboard", url: "https://dashboard.stripe.com" }
      ],
      warning: null
    },
    {
      id: 2,
      title: "Activate Your Account",
      description: "Switch from test mode to live mode in Stripe.",
      instructions: [
        "In Stripe Dashboard, look for the 'Activate your account' banner",
        "Complete all required business verification steps",
        "Add your bank account for payouts",
        "Review and accept Stripe's terms of service"
      ],
      links: [
        { text: "Stripe Account Settings", url: "https://dashboard.stripe.com/settings/account" }
      ],
      warning: "You cannot process real payments until your account is fully activated."
    },
    {
      id: 3,
      title: "Get Your Live API Keys",
      description: "Retrieve your live mode API keys from Stripe.",
      instructions: [
        "Go to Developers > API Keys in your Stripe Dashboard",
        "Make sure the 'Test mode' toggle is OFF (viewing live mode)",
        "Copy your 'Publishable key' (starts with pk_live_)",
        "Click 'Reveal live key' to see your 'Secret key' (starts with sk_live_)"
      ],
      links: [
        { text: "API Keys Page", url: "https://dashboard.stripe.com/apikeys" }
      ],
      warning: "Never share your Secret key publicly or commit it to version control!"
    },
    {
      id: 4,
      title: "Update Environment Variables",
      description: "Replace your test keys with live keys in the application.",
      instructions: [
        "Access your backend server's .env file",
        "Replace STRIPE_API_KEY with your live secret key",
        "Optionally set up STRIPE_WEBHOOK_SECRET for webhook verification",
        "Restart your backend server to apply changes"
      ],
      codeExample: `# In backend/.env
STRIPE_API_KEY=sk_live_your_live_secret_key_here
STRIPE_WEBHOOK_SECRET=whsec_your_webhook_secret_here`,
      warning: "Keep your test keys saved somewhere safe for development purposes."
    },
    {
      id: 5,
      title: "Configure Webhooks (Recommended)",
      description: "Set up webhooks for reliable payment event handling.",
      instructions: [
        "Go to Developers > Webhooks in Stripe Dashboard",
        "Click 'Add endpoint'",
        "Enter your webhook URL: your-domain.com/api/subscriptions/webhook",
        "Select events: checkout.session.completed, customer.subscription.updated, customer.subscription.deleted, invoice.payment_failed"
      ],
      links: [
        { text: "Webhooks Page", url: "https://dashboard.stripe.com/webhooks" }
      ],
      warning: null
    },
    {
      id: 6,
      title: "Test a Real Transaction",
      description: "Verify everything works with a small real payment.",
      instructions: [
        "Subscribe to your lowest tier using a real card",
        "Verify the payment appears in your Stripe Dashboard",
        "Check that your database subscription record was created",
        "Test cancellation flow to ensure it works correctly",
        "Refund the test payment if desired"
      ],
      links: [
        { text: "View Payments", url: "https://dashboard.stripe.com/payments" }
      ],
      warning: "Consider testing with a small amount first before announcing to users."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2 text-gray-800 hover:text-blue-600">
            <BrandLogo className="w-8 h-8" testId="stripe-guide-logo" />
            <span className="font-bold">Legacy Forge Family</span>
          </Link>
          <Link 
            to="/dashboard"
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Back to Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
              <FaStripe className="text-4xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Stripe Production Setup Guide</h1>
              <p className="text-indigo-200">Go live with real payments in 6 simple steps</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-2xl font-bold">15-30</p>
              <p className="text-sm text-indigo-200">Minutes to complete</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-2xl font-bold">6</p>
              <p className="text-sm text-indigo-200">Simple steps</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-2xl font-bold">$0</p>
              <p className="text-sm text-indigo-200">Setup cost</p>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex gap-3">
          <FaExclamationTriangle className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-amber-900 mb-1">Before You Begin</h3>
            <p className="text-amber-800 text-sm">
              Make sure you have completed testing in test mode. Once you switch to production, 
              all transactions will be real. Keep your test keys saved for future development work.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-6">
          {steps.map((step, index) => (
            <div key={step.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Step Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    {step.id}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{step.title}</h2>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="p-6">
                {/* Instructions */}
                <ul className="space-y-3 mb-4">
                  {step.instructions.map((instruction, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <FaCheckCircle className="text-emerald-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{instruction}</span>
                    </li>
                  ))}
                </ul>

                {/* Code Example */}
                {step.codeExample && (
                  <div className="bg-gray-900 rounded-lg p-4 mb-4 relative">
                    <button
                      onClick={() => copyToClipboard(step.codeExample, `code-${step.id}`)}
                      className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white"
                      title="Copy to clipboard"
                    >
                      {copiedItem === `code-${step.id}` ? <FaCheck className="text-emerald-400" /> : <FaCopy />}
                    </button>
                    <pre className="text-gray-300 text-sm overflow-x-auto">
                      <code>{step.codeExample}</code>
                    </pre>
                  </div>
                )}

                {/* Links */}
                {step.links && step.links.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {step.links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm font-medium"
                      >
                        {link.text}
                        <FaExternalLinkAlt className="text-xs" />
                      </a>
                    ))}
                  </div>
                )}

                {/* Warning */}
                {step.warning && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                    <FaExclamationTriangle className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{step.warning}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Security Best Practices */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mt-8">
          <div className="flex items-center gap-3 mb-4">
            <FaShieldAlt className="text-emerald-600 text-2xl" />
            <h2 className="text-xl font-bold text-emerald-900">Security Best Practices</h2>
          </div>
          <ul className="space-y-2 text-emerald-800">
            <li className="flex items-start gap-2">
              <FaCheckCircle className="text-emerald-600 mt-1 flex-shrink-0" />
              <span>Never expose your secret API key in client-side code or public repositories</span>
            </li>
            <li className="flex items-start gap-2">
              <FaCheckCircle className="text-emerald-600 mt-1 flex-shrink-0" />
              <span>Use environment variables for all sensitive configuration</span>
            </li>
            <li className="flex items-start gap-2">
              <FaCheckCircle className="text-emerald-600 mt-1 flex-shrink-0" />
              <span>Enable webhook signature verification in production</span>
            </li>
            <li className="flex items-start gap-2">
              <FaCheckCircle className="text-emerald-600 mt-1 flex-shrink-0" />
              <span>Regularly rotate your API keys if you suspect they've been compromised</span>
            </li>
            <li className="flex items-start gap-2">
              <FaCheckCircle className="text-emerald-600 mt-1 flex-shrink-0" />
              <span>Monitor your Stripe Dashboard for unusual activity</span>
            </li>
          </ul>
        </div>

        {/* Support Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="https://stripe.com/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FaStripe className="text-indigo-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Stripe Documentation</p>
                <p className="text-sm text-gray-600">Official guides and API reference</p>
              </div>
              <FaArrowRight className="text-gray-400 ml-auto" />
            </a>
            <Link
              to="/help-center"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaCreditCard className="text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Legacy Forge Help Center</p>
                <p className="text-sm text-gray-600">Platform-specific support</p>
              </div>
              <FaArrowRight className="text-gray-400 ml-auto" />
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
